import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import addSeconds from 'date-fns/addSeconds';
import format from 'date-fns/format';

import withPhotoUploadCache from '../hoc/withPhotoUploadCache';
import {
  getSortedVideoThumbnails,
  videoIsFailedUpload,
  videoIsProcessed,
} from '../../../../util/video';
import AlbumPhotoGroupIcon from '../../../../components/icons/AlbumPhotoGroupIcon';
import PlayButtonIcon from '../../../../components/icons/PlayButtonIcon';
import { getAsset } from '../../../../util/assets';

const propTypes = {
  linkTo: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
  video: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
  isDisabled: PropTypes.bool,
  className: PropTypes.string,
  thumbnails: PropTypes.arrayOf(
    PropTypes.shape({
      url: PropTypes.string.isRequired,
    }).isRequired,
  ),
};

const VideoCard = ({
  linkTo,
  video,
  className,
  isDisabled,
  actions,
  decorators,
}) => {
  const videoIsProcessedBool = videoIsProcessed(video);

  const disabled =
    isDisabled ||
    !video ||
    !video.embed_url ||
    (video?.thumbnails?.length ?? 0) === 0 ||
    !videoIsProcessedBool;

  const classes = classNames({
    'video-card': true,
    'video-card-disabled': disabled,
    [className]: !!className,
  });

  const thumbnailUrl = video.thumbnails
    ? // This gets the mid-quality thumb.
      getSortedVideoThumbnails(video.thumbnails)[
        Math.floor(video.thumbnails.length / 2)
      ]?.url
    : '';

  const cleanDate = new Date(0).setHours(0, 0, 0, 0);
  const secondsDate = addSeconds(cleanDate, video.duration ?? 0);

  if (!videoIsProcessedBool) {
    return (
      <div className={classes}>
        <figure className="video-card-thumb">
          <div className="video-card-thumb-empty">
            <img
              alt="Waldo logo"
              className="m-auto d-flex grayscale-faded"
              style={{
                width: '60px',
                height: 'auto',
              }}
              src={getAsset('/waldo-icon.svg')}
            />
            {videoIsFailedUpload(video) ? (
              <p className="text-center mt-2">
                There might have been an issue with this upload. Please delete
                and try again.
              </p>
            ) : (
              <p className="text-center mt-2">
                Your video is being processed&hellip;
              </p>
            )}
          </div>
        </figure>

        {actions && <div className="video-card-actions">{actions}</div>}

        <div className="video-card-footer">
          <h3 className="video-card-title">{video.name}</h3>
        </div>
      </div>
    );
  }

  return (
    <div className={classes}>
      <Link to={linkTo}>
        {thumbnailUrl ? (
          <Fragment>
            <figure className="video-card-thumb">
              <img src={thumbnailUrl} alt="" />
            </figure>
            <PlayButtonIcon size={50} className="video-play-button" />
          </Fragment>
        ) : (
          <figure className="video-card-thumb">
            <div className="video-card-thumb-empty">
              <AlbumPhotoGroupIcon color="#fff" />
            </div>
          </figure>
        )}

        {decorators && (
          <div className="video-card-decorators">{decorators}</div>
        )}
        {actions && <div className="video-card-actions">{actions}</div>}
        <div className="video-card-footer">
          <div className="d-flex flex-row">
            <h3 className="video-card-title">{video.name}</h3>
            <div className="video-card-duration">
              {format(secondsDate, 'mm:ss')}
            </div>
          </div>
        </div>
      </Link>
    </div>
  );
};

VideoCard.propTypes = propTypes;

export { VideoCard as VideoCardComponent };

export default withPhotoUploadCache(VideoCard);
